// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { array, bool, object, func, string } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, Trash, Pencil, Globe } from 'lucide-react';
import { uniqueId } from 'lodash';
import {
  DataTable,
  TableContainer,
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  ComposedModal,
  ModalHeader,
  ModalBody,
  SkeletonPlaceholder,
  SkeletonText,
  Toggle,
} from '@carbon/react';

import { NWTextInput, NWButton } from 'Components/shared';

const StyledIPTable = styled.div`
  .ipTable {
    .sectionHeader {
      .primary {
        span {
          font-size: 24px;
          padding-right: 5px;
        }
      }
    }

    .cds--data-table-container {
      border: none;
      
      .cds--data-table {
        font-size: 16px;
        border: none;
        overflow-x: scroll;

        thead {
          background: transparent;

          th {
            color: #3a3a3a;
            font-size: 16px;
            background: transparent;

            &:first-of-type, &:last-of-type {
              .cds--table-header-label {
                text-align: center;
              }
            }

            .cds--table-header-label {
              font-size: 16px;
              font-weight: 600;
              text-align: center;
            }
          }
        }

        tbody tr {
          td {
            background-color: #fff;
            text-align: center;

            .state {
              color: #999;
              font-weight: 600;

              &.active {
                color: #169001;
              }
            }

            .actions {
              display: flex;
              justify-content: center;
              gap: 10px;

              button {
                border: 1px solid #DDE6F2;
                padding: 0 7px !important;
                height: 30px;

                &.delete {
                  border: 1px solid #FEE7E5;
                  background-color: #FCF3F1;
                }

                svg {
                  stroke: #3a3a3a;
                }
              }
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .ipTable {
      .sectionHeader {
        flex-direction: column;
        gap: 10px;
        align-items: start;

        .primary {
          width: 100%;
          max-width: none;
        }
      }

      .cds--data-table-container {
        padding: 16px;

        .cds--data-table {
          thead {
            display: none;
          }

          tbody {
            background: #fff;

            tr {
              display: flex;
              flex-direction: column;
              height: auto;
              margin-top: 20px;
              border: 1px solid #e2e2e2;
              position: relative;

              td {
                border: transparent;
                text-align: left;

                :first-of-type {
                  background: #F5F7F9;
                  position: relative;
                }
              }
            }
          }
        }
      }
    }
  }
`;

const StyledModal = styled(ComposedModal)`
  .cds--modal-content {
    padding: 0;
    margin: 0;
  }

  .footer {
    padding: 0 24px 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    flex-direction: column;

    .ipAddress {
      width: 100%;

      .cds--text-input-wrapper {
        margin: 0 0 10px 0;
      }
    }

    .ipStatus {
      width: 100%;

      .cds--toggle-input:checked + .cds--toggle-input__label > .cds--toggle__switch::before {
        background-color: #0F233C;
      }

      .cds--toggle__switch {
        margin: 0 0 10px 0;
      }
    }

    .message {
      span {
        font-weight: 600;
      }
    }

    .actions {
      display: flex;
      gap: 10px;
      width: 100%;
      margin-top: 10px;

      button {
        flex: 1;
        max-width: none;
      }
    }
  }
`;

const StyledTableCell = styled(TableCell)`
  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    &[data-label="ipAddress"],
    &[data-label="isActive"],
    &[data-label="createdAt"],
    &[data-label="actions"] {    
      display: flex;
      align-items: center;
      height: 40px;
      padding-left: 120px;

      &:before {
        content: "${ props => props.name }:";
        position: absolute;
        left: 10px;
        width: 140px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 600;
      }
    }
  }
`;

const StyledSkeletonPlaceholder = styled(SkeletonPlaceholder)`
  height: 3rem;
  width: 3rem;
`;

const IPTable = ({
  IPAddressList,
  IPAddressListLoading,
  toggleIPAddress,
  activateLoading,
  handleSubmit,
  isRequestPending,
  register,
  setValue,
  rhfErrors,
  validators,
  isActiveValue,
  addModalOpen,
  addModalClose,
  handleModalOpen,
  ipAddressValue,
  deleteModalOpen,
  handleDeleteModalOpen,
  deleteModalClose,
  handleDelete,
  ipAddressToDelete,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const headerData = useMemo(() => [
    { key: 'ipAddress', header: t('common.ipAddress') },
    { key: 'isActive', header: t('common.status') },
    { key: 'createdAt', header: t('common.createdAt') },
    { key: 'actions', header: t('common.actions') },
  ], [t]);

  const renderCell = cell => {
    if (cell.info.header === 'isActive') {
      return <span className={ `state ${ cell.value && 'active' }` }>{ cell.value ? t('common.active') : t('common.inactive') }</span>;
    }

    if (cell.info.header === 'actions') {
      const { id, isActive } = cell.value;
      return (
        <div className="actions">
          <NWButton
            kind="primary"
            loading={ id === activateLoading }
            onClick={ () => toggleIPAddress(id, isActive) }
          >
            { isActive ? <Pause /> : <Play /> }
          </NWButton>
          <NWButton
            kind="primary"
            onClick={ () => handleModalOpen(id) }
          >
            <Pencil />
          </NWButton>
          <NWButton
            kind="primary"
            className="delete"
            onClick={ () => handleDeleteModalOpen(id) }
          >
            <Trash />
          </NWButton>
        </div>
      );
    }

    return cell.value;
  };

  const mapHeaderToLabel = label => {
    let mappedLabel = label;
    if (label === 'isActive') {
      mappedLabel = 'status';
    }
    return t(`common.${mappedLabel}`);
  };

  return (
    <StyledIPTable theme={ theme }>
      <StyledModal
        open={ addModalOpen }
        size="sm"
        onClose={ addModalClose }
        selectorPrimaryFocus=".cds--modal-content"
      >
        <ModalHeader>
          <h4>{ t('profile.addIPAddress') }</h4>
        </ModalHeader>
        <ModalBody>
          <div className="footer">
            <NWTextInput
              className="ipAddress"
              light
              required
              labelText={ t('profile.ipAddress') }
              id="ipAddress"
              name="ipAddress"
              invalid={ !!rhfErrors?.ipAddress?.message.length }
              invalidText={ rhfErrors.ipAddress?.message }
              { ...register('ipAddress', { ...validators.ipAddress }) }
            />
            <Toggle
              id="ipStatus"
              className="ipStatus"
              labelA={ t('common.inactive') }
              labelB={ t('common.active') }
              { ...register('isActive') }
              onToggle={ value => setValue('isActive', value) }
              toggled={ isActiveValue }
            />
            <div className="actions">
              <NWButton
                kind="danger"
                onClick={ handleSubmit }
                disabled={ isRequestPending || !ipAddressValue?.length || rhfErrors.ipAddress?.message.length }
                loading={ isRequestPending }
              >
                { t('common.save') }
              </NWButton>
              <NWButton
                kind="danger--tertiary"
                onClick={ addModalClose }
              >
                { t('common.cancel') }
              </NWButton>
            </div>
          </div>
        </ModalBody>
      </StyledModal>
      <StyledModal
        open={ deleteModalOpen }
        size="sm"
        onClose={ deleteModalClose }
        selectorPrimaryFocus=".cds--modal-content"
      >
        <ModalHeader>
          <h4>{ t('profile.deleteIPAddress') }</h4>
        </ModalHeader>
        <ModalBody>
          <div className="footer">
            <div className="message">
              <p dangerouslySetInnerHTML={ { __html: t('profile.deleteIPAddressPrompt', { ipAddress: ipAddressToDelete.ipAddress }) } } />
            </div>
            <div className="actions">
              <NWButton
                kind="danger"
                onClick={ handleDelete }
                loading={ isRequestPending }
              >
                { t('common.delete') }
              </NWButton>
              <NWButton
                kind="danger--tertiary"
                onClick={ deleteModalClose }
              >
                { t('common.cancel') }
              </NWButton>
            </div>
          </div>
        </ModalBody>
      </StyledModal>
      { IPAddressListLoading ? (
        <section key={ uniqueId('section') }>
          <h4><SkeletonText /></h4>
          { Array.from({ length: 3 }, () => (
            <div className="file" key={ uniqueId('apiInformation') }>
              <div className="fileIcon">
                <StyledSkeletonPlaceholder />
              </div>
              <div className="fileName skeleton">
                <SkeletonText paragraph lineCount={ 2 } />
              </div>
            </div>
          ))}
        </section>
      ) : (
        <section className="ipTable">
          <div className="sectionHeader">
            <h4><Globe size={ 20 } /> { t('profile.ipAddressAccess') }</h4>
            <NWButton
              kind="primary"
              onClick={ () => handleModalOpen() }
            >
              <span>+</span>
              { t('profile.addIPAddress') }
            </NWButton>
          </div>
          <div className="sectionBody">
            <DataTable rows={ IPAddressList } headers={ headerData }>
              {({ rows, headers, getHeaderProps, getTableProps }) => (
                <TableContainer>
                  <Table { ...getTableProps() }>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader
                            { ...getHeaderProps({ header }) }
                          >
                            { header.header }
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      { IPAddressList.length === 0 && (<TableRow><TableCell colSpan={ headers.length + 1 }>{ t('common.noResults') }</TableCell></TableRow>) }
                      { rows.map(row => (
                        <TableRow key={ row.id }>
                          { row.cells.map(cell => (
                            <StyledTableCell
                              data-label={ cell.info.header }
                              name={ () => mapHeaderToLabel(cell.info.header) }
                              key={ cell.id }
                            >
                              { renderCell(cell) }
                            </StyledTableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>
          </div>
        </section>
      )}
    </StyledIPTable>
  );
};

IPTable.propTypes = {
  IPAddressList: array,
  IPAddressListLoading: bool,
  toggleIPAddress: func,
  activateLoading: bool,
  handleSubmit: func,
  isRequestPending: bool,
  register: func,
  setValue: func,
  rhfErrors: object,
  validators: object,
  isActiveValue: bool,
  addModalOpen: bool,
  addModalClose: func,
  handleModalOpen: func,
  ipAddressValue: string,
  deleteModalOpen: bool,
  handleDeleteModalOpen: func,
  deleteModalClose: func,
  handleDelete: func,
  ipAddressToDelete: object,
};

IPTable.displayName = 'IPTableComponent';

export default IPTable;
